import React, { useState } from "react";
import { Breadcrumbs, StepIndicator, Container } from "@telia/teddy";
import classnames from "classnames";
import { connect } from "react-redux";
import { useLocation, Outlet } from "react-router-dom";
import { FWA_LABELS, FWA_LINKS } from "../../../../../../constants";
import PAGES from "../../../../../../constants/pages";
import { RootState } from "../../../../../../models";
import { ConnectedAlert, ProgressBar } from "../../../../components";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import ContinueSection from "../../molecules/ContinueSection/ContinueSection";
import "./FwaPage.less";

const FWA_REFERRER_KEY = "fwa_referrer";

function getInternetUrl(): string {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return PAGES.FWA.URL;
    }
    try {
        const stored = window.sessionStorage.getItem(FWA_REFERRER_KEY);
        if (stored) return stored;

        const referrer = document.referrer;
        if (!referrer) return PAGES.FWA.URL;

        const url = new URL(referrer);
        if (url.pathname.startsWith(PAGES.FWA.URL) && !url.pathname.startsWith(PAGES.FWA.ADDRESS_SEARCH.URL)) {
            window.sessionStorage.setItem(FWA_REFERRER_KEY, referrer);
            return referrer;
        }
    } catch {
        // ignore unavailable browser APIs and invalid referrer URL
    }
    return PAGES.FWA.URL;
}

function locationToFwaLinkIndex(path: string): number {
    const links = FWA_LINKS as readonly string[];
    const exactIndex = links.indexOf(path);
    if (exactIndex >= 0) return exactIndex;

    let matchedIndex = -1;
    let matchedLength = -1;

    links.forEach((link, index) => {
        if (path.startsWith(link) && link.length > matchedLength) {
            matchedIndex = index;
            matchedLength = link.length;
        }
    });

    return matchedIndex;
}

function locationToStepIndicatorIndex(path: string) {
    const index = locationToFwaLinkIndex(path);
    return Math.max(index, 0);
}

function locationToLabel(path: string): string {
    if (path === PAGES.FWA.ADDRESS_SEARCH.URL) return "Adressesøk";
    const index = locationToFwaLinkIndex(path);
    return FWA_LABELS[index] ?? "";
}

interface FwaPageProps {
    className?: string;
    isFetchingData: boolean;
}

const FwaPage: React.FC<FwaPageProps> = ({ className = "", isFetchingData }) => {
    const location = useLocation();
    const isAddressSearch = location.pathname === PAGES.FWA.ADDRESS_SEARCH.URL;
    const [internettUrl] = useState(getInternetUrl);
    const currentLabel = locationToLabel(location.pathname);
    return (
        <div
            className={classnames("fwa-page", {
                [className]: className,
            })}
        >
            <Container className="fwa-page__container">
                <div className="fwa-page__breadcrumbs">
                    <Breadcrumbs>
                        <Breadcrumbs.List>
                            <Breadcrumbs.Item>
                                <Breadcrumbs.Link href="/" home>
                                    Forsiden
                                </Breadcrumbs.Link>
                            </Breadcrumbs.Item>
                            <Breadcrumbs.Separator />
                            <Breadcrumbs.Item>
                                <Breadcrumbs.Link href={internettUrl}>Internett</Breadcrumbs.Link>
                            </Breadcrumbs.Item>
                            <Breadcrumbs.Separator />
                            {isAddressSearch ? (
                                <Breadcrumbs.Item>
                                    <Breadcrumbs.Page>{currentLabel}</Breadcrumbs.Page>
                                </Breadcrumbs.Item>
                            ) : (
                                <>
                                    <Breadcrumbs.Item>
                                        <Breadcrumbs.Link href={PAGES.FWA.ADDRESS_SEARCH.URL}>
                                            Adressesøk
                                        </Breadcrumbs.Link>
                                    </Breadcrumbs.Item>
                                    <Breadcrumbs.Separator />
                                    <Breadcrumbs.Item>
                                        <Breadcrumbs.Page>{currentLabel}</Breadcrumbs.Page>
                                    </Breadcrumbs.Item>
                                </>
                            )}
                        </Breadcrumbs.List>
                    </Breadcrumbs>
                </div>
            </Container>
            {!isAddressSearch && (
                <div className="fwa-page__step-indicator">
                    <StepIndicator
                        index={locationToStepIndicatorIndex(location.pathname)}
                        numberOfSteps={4}
                        labels={FWA_LABELS}
                        links={FWA_LINKS}
                        kind="purple"
                    />
                </div>
            )}
            <ProgressBar isFetchingData={isFetchingData} backgroundColor="white" />
            <ConnectedAlert />
            <div className="fwa-page__content">
                <Outlet />
                <ContinueSection path={location.pathname} />
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
});

export default connect(mapStateToProps)(FwaPage);
